import type { Vec2 } from './vec'

// Stable, lowercase, namespaced dotted content ids. Never renamed/reused. The
// canonical id rows live in client/content/data.ts, the single source the sim
// core reads.
export type ContentId = string

/** Axis-aligned bounding box, in world units. */
export interface Aabb {
  min: Vec2
  max: Vec2
}

/**
 * A grid tile index. Placement targets a tile, not a world/pixel coordinate: the
 * sim converts tile to world deterministically, so a placement is inherently
 * viewport-independent and the command log stays stable across clients.
 */
export interface Tile {
  tx: number
  ty: number
}

/**
 * A building is a static obstacle that also owns its inventory and access-slot
 * occupancy. In the spike it is a plain-data stub; the real recipe-derived model
 * comes later.
 */
export interface Building {
  id: ContentId
  box: Aabb
  /** World positions colonists stand at to interact; naive queue = one at a time. */
  slots: Slot[]
  /** itemId -> count held. */
  inventory: Map<ContentId, number>
}

/** A single access slot. `occupant` is a unit id, or null when free. */
export interface Slot {
  pos: Vec2
  occupant: number | null
}

/**
 * A moving square. In the perf spike these are anonymous movers; in the pull
 * spike a single unit is the hauling colonist. `radius` is its half-extent for
 * non-penetration against building boxes.
 */
export interface Unit {
  id: number
  pos: Vec2
  target: Vec2 | null
  radius: number
  speed: number
}

export function makeAabb(x: number, y: number, w: number, h: number): Aabb {
  return { min: { x, y }, max: { x: x + w, y: y + h } }
}

export function center(box: Aabb): Vec2 {
  return { x: (box.min.x + box.max.x) / 2, y: (box.min.y + box.max.y) / 2 }
}

/** True when point p lies strictly inside box inflated by `pad` on every side. */
export function pointInInflated(p: Vec2, box: Aabb, pad: number): boolean {
  return (
    p.x > box.min.x - pad &&
    p.x < box.max.x + pad &&
    p.y > box.min.y - pad &&
    p.y < box.max.y + pad
  )
}
